import { Link } from "react-router"
import type { Locale } from "../../content/types"
import { company, formatDisplayLocation } from "../../content/company"
import { getPagePath } from "../../content/registry"
import { services } from "../../content/services"
import { locations } from "../../content/locations"
import { ui } from "../../content/ui"

interface FooterProps {
  locale: Locale | null
}

export default function Footer({ locale }: FooterProps) {
  const currentLocale: Locale = locale ?? "nl"

  const navLinks = [
    { label: ui.nav.home[currentLocale], path: getPagePath("home", currentLocale)! },
    { label: ui.nav.about[currentLocale], path: getPagePath("about", currentLocale)! },
    { label: ui.nav.cases[currentLocale], path: getPagePath("cases", currentLocale)! },
    { label: ui.nav.services[currentLocale], path: getPagePath("services", currentLocale)! },
    { label: ui.nav.insights[currentLocale], path: getPagePath("insights", currentLocale)! },
    { label: ui.nav.contact[currentLocale], path: getPagePath("contact", currentLocale)! },
  ]

  return (
    <footer className="bg-site-bg border-t border-border-subtle mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <Link to={getPagePath("home", currentLocale)!} className="inline-block mb-4">
              <img
                src={company.logo}
                alt="Schmeitz De Groen AI Labs"
                className="h-8 w-auto"
                width="160"
                height="32"
              />
            </Link>
            <p className="text-text-secondary text-sm leading-relaxed max-w-md mb-6">{ui.footer.tagline[currentLocale]}</p>
            <div className="flex flex-col gap-2 text-text-secondary text-sm">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{formatDisplayLocation(currentLocale)}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span>
                  {ui.footer.kvk[currentLocale]}: {company.kvk}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation Column */}
          <div>
            <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide mb-4">
              {ui.footer.navigationTitle[currentLocale]}
            </h2>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-text-secondary hover:text-button-primary transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Locations */}
            <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide mb-4 mt-8">
              {ui.footer.locationsTitle[currentLocale]}
            </h2>
            <ul className="space-y-3">
              {locations.map((location) => {
                const slug = location.slug[currentLocale]
                if (!slug) return null
                return (
                  <li key={location.id}>
                    <Link
                      to={`/${currentLocale}/${slug}/`}
                      className="text-text-secondary hover:text-button-primary transition-colors text-sm"
                    >
                      {location.h1?.[currentLocale] ?? location.cityOrRegion}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Services Column */}
          <div>
            <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide mb-4">
              {ui.footer.servicesTitle[currentLocale]}
            </h2>
            <ul className="space-y-3">
              {services.map((service) => {
                const path = service.slug[currentLocale]
                if (!path) return null
                const fullPath =
                  currentLocale === "nl" ? `/nl/diensten/${path}/` : `/en/services/${path}/`
                return (
                  <li key={service.id}>
                    <Link
                      to={fullPath}
                      className="text-text-secondary hover:text-button-primary transition-colors text-sm"
                    >
                      {service.title[currentLocale].split("|")[0].trim()}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h2 className="text-text-primary font-bold text-sm uppercase tracking-wide mb-4">
              {ui.footer.contactTitle[currentLocale]}
            </h2>
            <ul className="space-y-3">
              <li>
                <a
                  href={`mailto:${company.email}`}
                  className="text-text-secondary hover:text-button-primary transition-colors text-sm flex items-start gap-2"
                >
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="break-all">{company.email}</span>
                </a>
              </li>
              {company.phoneNumbers.map((phone) => (
                <li key={phone.href}>
                  <a
                    href={phone.href}
                    className="text-text-secondary hover:text-button-primary transition-colors text-sm flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {phone.display}
                  </a>
                </li>
              ))}
              <li className="pt-2 flex flex-col gap-3">
                <a
                  href={company.socialProfiles.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-text-secondary hover:text-[#0A66C2] transition-colors text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
                <a
                  href={company.socialProfiles.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-text-secondary hover:text-[#E4405F] transition-colors text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                  Instagram
                </a>
                <a
                  href={company.socialProfiles.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-text-secondary hover:text-[#1877F2] transition-colors text-sm"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.248h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border-subtle">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-text-secondary text-sm">
              © {new Date().getFullYear()} {company.name}. {ui.footer.allRightsReserved[currentLocale]}
            </p>
            <div className="flex items-center gap-6">
              <Link
                to={getPagePath("privacy", currentLocale)!}
                className="text-text-secondary hover:text-button-primary transition-colors text-sm"
              >
                {ui.privacyLink[currentLocale]}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
